// frontend/src/pages/Expenses/ExpensesList.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Edit, Trash2, Plus, Building, User,
  Filter, Search, AlertCircle, Loader2,
  Eye, Globe, RefreshCw, CheckCircle, Tag,
  Calendar, DollarSign, FileText, CreditCard, Layers, Wallet
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./ExpensesList.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const ExpensesList = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Format amount with company currency
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return formatCurrency(0, companyCurrency);
    return formatCurrency(amount, companyCurrency);
  };

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [companies, setCompanies] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);

  // For system admins
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  useEffect(() => {
    fetchExpenses();

    if (isSystemAdmin) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, startDate, endDate]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);

      const params = {};

      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }

      if (activeOnly) {
        params.status = 'active';
      } else {
        params.status = 'all';
      }

      if (startDate) {
        params.startDate = startDate;
      }

      if (endDate) {
        params.endDate = endDate;
      }

      const response = await axios.get(`${url}/api/expense`, {
        headers: getAuthHeaders(),
        params
      });

      if (response.data.success) {
        setExpenses(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch expenses");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch expenses");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleDelete = async (id, expenseName) => {
    if (!window.confirm(`Are you sure you want to delete expense "${expenseName}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${url}/api/expense/${id}`, {
        headers: getAuthHeaders()
      });

      if (response.data.success) {
        toast.success(`Expense "${expenseName}" deleted successfully`);
        fetchExpenses();
      } else {
        toast.error(response.data.message || "Failed to delete expense");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete expense");
    }
  };

  const handleViewExpense = (expense) => {
    const details = `
Expense Details:

Expense: ${expense.expense_name}
Date: ${new Date(expense.expense_date).toLocaleDateString()}
Amount: ${formatAmount(expense.amount)}
Invoice #: ${expense.invoice_number || "N/A"}
Expense Type (Debit): ${expense.expense_account?.account_name || "N/A"}
Payment Account (Credit): ${expense.account?.account_name || "N/A"}
Payment Method: ${expense.payment_method || "N/A"}
Reference #: ${expense.reference_number || "N/A"}
Description: ${expense.description || "Not provided"}
Status: ${expense.status === 'active' ? 'Active' : expense.status === 'paid' ? 'Paid' : 'Pending'}
Created By: ${expense.created_by?.name || expense.created_by?.Usercomp_sure_name || "System"}
Created: ${new Date(expense.created_at).toLocaleDateString()}
    `;
    alert(details);
  };

  const handleEditExpense = (id) => {
    navigate(`/expense/edit/${id}`);
  };

  // Filter expenses based on search term
  const filteredExpenses = expenses.filter(expense =>
    expense.expense_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.expense_account?.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    expense.account?.account_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const totalExpenses = expenses.length;
  const totalAmount = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const activeExpenses = expenses.filter(e => e.status === 'active').length;

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading expenses...</p>
      </div>
    );
  }

  return (
    <div className="expenses-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Expenses List</h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>

        <div className="header-actions">
          <button
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filter
          </button>

          <button
            onClick={() => navigate("/AddExpenses")}
            className="add-button"
          >
            <Plus size={16} /> Add Expense
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon">
            <Tag size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{totalExpenses}</div>
            <div className="stat-label">Total Expenses</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon active">
            <DollarSign size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{formatAmount(totalAmount)}</div>
            <div className="stat-label">Total Amount</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon active">
            <CheckCircle size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{activeExpenses}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-container">
          <div className="filter-group">
            <label>Filter by Company:</label>
            <div className="select-wrapper">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={loadingCompanies || !isSystemAdmin}
              >
                <option value="">All Companies</option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.company_name} ({comp.company_code})
                  </option>
                ))}
              </select>
              <Building size={16} className="select-icon" />
            </div>
          </div>

          <div className="filter-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="filter-group">
            <label>End Date:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="filter-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              <span>Show Active Only</span>
            </label>
          </div>

          <div className="filter-actions">
            <button
              className="clear-filters"
              onClick={() => {
                setSelectedCompany("");
                setStartDate("");
                setEndDate("");
                setActiveOnly(true);
              }}
            >
              Clear Filters
            </button>

            <button
              className="apply-filters"
              onClick={() => {
                fetchExpenses();
                setShowFilters(false);
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search expenses by name, invoice, description, or account..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => setSearchTerm("")}
            >
              ✕
            </button>
          )}
        </div>

        <div className="search-actions">
          <button
            className="refresh-btn"
            onClick={fetchExpenses}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Expense Name</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Invoice #</th>
              <th>Expense Type</th>
              <th>Payment Account</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredExpenses.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdmin ? 10 : 9} className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Tag size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {searchTerm || selectedCompany
                        ? "No expenses found matching your criteria"
                        : "No expenses found"
                      }
                    </p>
                    <button
                      className="add-button"
                      onClick={() => navigate("/AddExpenses")}
                    >
                      <Plus size={16} /> Create First Expense
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredExpenses.map((expense, index) => (
                <tr key={expense._id}>
                  <td>{index + 1}</td>

                  <td>
                    <div className="expense-name-cell">
                      <Tag size={14} className="mr-2" />
                      <div>
                        <strong>{expense.expense_name}</strong>
                        {expense.expense_id && (
                          <div className="expense-id">ID: {expense.expense_id}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="date-cell">
                      <Calendar size={12} className="mr-2" />
                      {new Date(expense.expense_date).toLocaleDateString()}
                    </div>
                  </td>

                  <td>
                    <div className="amount-cell">
                      <DollarSign size={12} className="mr-2" />
                      {formatAmount(expense.amount)}
                    </div>
                  </td>

                  <td>
                    <div className="invoice-cell">
                      <FileText size={12} className="mr-2" />
                      {expense.invoice_number || "—"}
                    </div>
                  </td>

                  {/* ✅ NEW: Expense Type (Debit side) */}
                  <td>
                    <div className="account-cell">
                      <Layers size={12} className="mr-2" />
                      {expense.expense_account?.account_name || "—"}
                      {expense.expense_account?.account_code && (
                        <div className="expense-id">{expense.expense_account.account_code}</div>
                      )}
                    </div>
                  </td>

                  {/* Payment Account (Credit side) */}
                  <td>
                    <div className="account-cell">
                      <Wallet size={12} className="mr-2" />
                      {expense.account?.account_name || "—"}
                      {expense.account?.account_code && (
                        <div className="expense-id">{expense.account.account_code}</div>
                      )}
                    </div>
                  </td>

                  {isSystemAdmin && (
                    <td>
                      <div className="company-cell">
                        <Building size={12} className="mr-2" />
                        <div>
                          <div>{expense.company_id?.company_name || "N/A"}</div>
                        </div>
                      </div>
                    </td>
                  )}

                  <td>
                    <span className={`status-badge ${expense.status}`}>
                      {expense.status === 'active' ? 'Active' :
                       expense.status === 'paid' ? 'Paid' : 'Inactive'}
                    </span>
                  </td>

                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleViewExpense(expense)}
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>

                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditExpense(expense._id)}
                        title="Edit Expense"
                        disabled={expense.status === 'inactive'}
                      >
                        <Edit size={14} />
                      </button>

                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(expense._id, expense.expense_name)}
                        title="Delete Expense"
                        disabled={expense.status === 'inactive'}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExpensesList;
